import React, { useState } from 'react';
import { Database, Key, Link as LinkIcon, Hash, Type, Calendar, ToggleLeft, FileJson, Shield, Users, Target, ClipboardList, BarChart3, Sparkles, CreditCard, Briefcase, LineChart, GraduationCap, History, Settings, Info } from 'lucide-react';
type Domain = 'conventions' | 'identity' | 'content' | 'assessments' | 'responses' | 'reporting' | 'future_billing' | 'future_marketplace' | 'future_benchmarking' | 'future_coaching' | 'audit';
interface Column {
  name: string;
  type: string;
  isPk?: boolean;
  isFk?: boolean;
  isNullable?: boolean;
  isUnique?: boolean;
  description: string;
}
interface TableDef {
  name: string;
  description: string;
  columns: Column[];
  indexes?: string[];
}
export function DatabaseSchema() {
  const [activeDomain, setActiveDomain] = useState<Domain>('conventions');
  const domains = [{
    id: 'conventions',
    label: 'Conventions & Optimization',
    icon: Settings,
    category: 'Overview'
  }, {
    id: 'identity',
    label: 'Identity & Access',
    icon: Users,
    category: 'Core Domains'
  }, {
    id: 'content',
    label: 'Content Library',
    icon: Target,
    category: 'Core Domains'
  }, {
    id: 'assessments',
    label: 'Assessments',
    icon: ClipboardList,
    category: 'Core Domains'
  }, {
    id: 'responses',
    label: 'Responses & Scoring',
    icon: Hash,
    category: 'Core Domains'
  }, {
    id: 'reporting',
    label: 'Reporting & AI',
    icon: Sparkles,
    category: 'Core Domains'
  }, {
    id: 'future_billing',
    label: 'Subscriptions (Future)',
    icon: CreditCard,
    category: 'Future Modules'
  }, {
    id: 'future_marketplace',
    label: 'Consultant Marketplace',
    icon: Briefcase,
    category: 'Future Modules'
  }, {
    id: 'future_benchmarking',
    label: 'Benchmarking',
    icon: LineChart,
    category: 'Future Modules'
  }, {
    id: 'future_coaching',
    label: 'AI Coaching',
    icon: GraduationCap,
    category: 'Future Modules'
  }, {
    id: 'audit',
    label: 'Audit & System',
    icon: History,
    category: 'System'
  }] as const;
  const groupedDomains = domains.reduce((acc, domain) => {
    if (!acc[domain.category]) {
      acc[domain.category] = [];
    }
    acc[domain.category].push(domain);
    return acc;
  }, {} as Record<string, (typeof domains)[number][]>);
  return <div className="space-y-6 animate-in fade-in duration-500 pb-12" data-id="element-1208" data-mp="25fbmmrx75yjc">
      {/* Header */}
      <div data-id="element-1209" data-mp="25h67y7cdrsny">
        <h1 className="text-2xl font-semibold text-slate-900 flex items-center" data-id="element-1210" data-mp="20g8jjd0s22e0">
          <Database className="h-6 w-6 mr-3 text-slate-700" data-id="element-1211" data-mp="20il7q7x4p8zi" />
          Database Schema
        </h1>
        <p className="text-slate-500 mt-1" data-id="element-1212" data-mp="3lm58588q8hm">
          Complete relational schema documentation for the Oversight Insights
          platform.
        </p>
      </div>

      <div className="flex flex-col md:flex-row gap-8" data-id="element-1213" data-mp="67mqlh8x38xi">
        {/* Left Sidebar Nav */}
        <div className="w-full md:w-64 shrink-0" data-id="element-1214" data-mp="b66hlw2dbawq">
          <nav className="space-y-6 sticky top-6" data-id="element-1215" data-mp="fy7ot3koibfw">
            {Object.entries(groupedDomains).map(([category, items]) => <div key={category} data-id="element-1216" data-mp="kxkb9n7n1ghy">
                <h3 className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-3 px-3" data-id="element-1217" data-mp="kwrf1g6b29e2">
                  {category}
                </h3>
                <div className="space-y-1" data-id="element-1218" data-mp="3odhm7ydmishc">
                  {items.map(item => {
                const Icon = item.icon;
                const isActive = activeDomain === item.id;
                return <button key={item.id} onClick={() => setActiveDomain(item.id)} className={`w-full flex items-center px-3 py-2 text-sm font-medium rounded-md transition-colors ${isActive ? 'bg-slate-900 text-white' : 'text-slate-600 hover:bg-slate-100 hover:text-slate-900'}`} data-id="element-1219" data-mp="27ul5detliwd">
                        <Icon className={`mr-3 h-4 w-4 ${isActive ? 'text-slate-300' : 'text-slate-400'}`} data-id="element-1220" data-mp="1767yp1uo0ksq" />
                        {item.label}
                      </button>;
              })}
                </div>
              </div>)}
          </nav>
        </div>

        {/* Main Content Area */}
        <div className="flex-1 min-w-0" data-id="element-1221" data-mp="1q4two7uvxvsf">
          <div className="bg-white border border-slate-200 rounded-xl shadow-sm overflow-hidden min-h-[600px]" data-id="element-1222" data-mp="1q3sms6plvfca">
            {activeDomain === 'conventions' && <ConventionsSection data-id="element-1223" data-mp="1ywvtlw5qwtxy" />}
            {activeDomain === 'identity' && <IdentitySection data-id="element-1224" data-mp="1yxnr5ciwl1qv" />}
            {activeDomain === 'content' && <ContentSection data-id="element-1225" data-mp="2dtd82w98eooq" />}
            {activeDomain === 'assessments' && <AssessmentsSection data-id="element-1226" data-mp="28v1x2i77a84x" />}
            {activeDomain === 'responses' && <ResponsesSection data-id="element-1227" data-mp="28wwj5zu7k6al" />}
            {activeDomain === 'reporting' && <ReportingSection data-id="element-1228" data-mp="2sglivvymos06" />}
            {activeDomain === 'future_billing' && <BillingSection data-id="element-1229" data-mp="2wi6uzf3c929b" />}
            {activeDomain === 'future_marketplace' && <MarketplaceSection data-id="element-1230" data-mp="2wgcimp64uigu" />}
            {activeDomain === 'future_benchmarking' && <BenchmarkingSection data-id="element-1231" data-mp="2ii5sjv7fdmrq" />}
            {activeDomain === 'future_coaching' && <CoachingSection data-id="element-1232" data-mp="2ngphm4hbfeob" />}
            {activeDomain === 'audit' && <AuditSection data-id="element-1233" data-mp="28sw18hkeo84o" />}
          </div>
        </div>
      </div>
    </div>;
}
// --- Helper Components ---
function TableCard({
  table
}: {
  table: TableDef;
}) {
  return <div className="border border-slate-200 rounded-xl overflow-hidden mb-8 bg-white" data-id="element-1234" data-mp="2dkvzm5ktusz5">
      <div className="bg-slate-50 border-b border-slate-200 p-4 flex justify-between items-start" data-id="element-1235" data-mp="1ztr156t746qe">
        <div data-id="element-1236" data-mp="24rspa65haw8q">
          <h3 className="text-lg font-semibold text-slate-900 font-mono" data-id="element-1237" data-mp="24p6gy4arkcpg">
            {table.name}
          </h3>
          <p className="text-sm text-slate-500 mt-1" data-id="element-1238" data-mp="1ut3f6zjtqg31">{table.description}</p>
        </div>
      </div>
      <div className="overflow-x-auto" data-id="element-1239" data-mp="1bohrxw15i0l2">
        <table className="w-full text-sm text-left" data-id="element-1240" data-mp="1bpjfgzzcsfjo">
          <thead className="text-xs text-slate-500 uppercase bg-slate-50/50 border-b border-slate-200" data-id="element-1241" data-mp="1bqaxt93xwgs0">
            <tr data-id="element-1242" data-mp="11yoip1ohdxjz">
              <th className="px-4 py-3 font-medium w-1/3" data-id="element-1243" data-mp="11y59c9cwchhk">Column</th>
              <th className="px-4 py-3 font-medium w-1/4" data-id="element-1244" data-mp="11wc1iqqjrr7z">Type</th>
              <th className="px-4 py-3 font-medium w-1/12 text-center" data-id="element-1245" data-mp="x154g1rs9ks1">
                Constraints
              </th>
              <th className="px-4 py-3 font-medium" data-id="element-1246" data-mp="1ul9jsturdo0q">Description</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100" data-id="element-1247" data-mp="1pm6d74z4r9qn">
            {table.columns.map((col, i) => <tr key={i} className="hover:bg-slate-50/50" data-id="element-1248" data-mp="1kr945y1up0dr" data-mpi={i}>
                <td className="px-4 py-3 font-mono text-slate-700 font-medium" data-id="element-1249" data-mp="1fpa2cslrebix">
                  {col.name}
                </td>
                <td className="px-4 py-3 font-mono text-slate-500 text-xs" data-id="element-1250" data-mp="97wog9qz7iza">
                  {col.type}
                </td>
                <td className="px-4 py-3 text-center" data-id="element-1251" data-mp="2fy9pgtrzja3k">
                  <div className="flex items-center justify-center gap-1" data-id="element-1252" data-mp="2fzupgyx2jl8t">
                    {col.isPk && <span className="bg-amber-100 text-amber-800 text-[10px] px-1.5 py-0.5 rounded font-bold" title="Primary Key" data-id="element-1253" data-mp="2kxmhru1ml9dj">
                        PK
                      </span>}
                    {col.isFk && <span className="bg-indigo-100 text-indigo-800 text-[10px] px-1.5 py-0.5 rounded font-bold" title="Foreign Key" data-id="element-1254" data-mp="2zolmuv395fsd">
                        FK
                      </span>}
                    {col.isUnique && <span className="bg-emerald-100 text-emerald-800 text-[10px] px-1.5 py-0.5 rounded font-bold" title="Unique" data-id="element-1255" data-mp="1e9frvfn9xqsf">
                        UQ
                      </span>}
                    {col.isNullable && <span className="bg-slate-100 text-slate-500 text-[10px] px-1.5 py-0.5 rounded font-bold" title="Nullable" data-id="element-1256" data-mp="3g5ez21p9riu5">
                        NULL
                      </span>}
                  </div>
                </td>
                <td className="px-4 py-3 text-slate-600 text-xs" data-id="element-1257" data-mp="326xda9ixcgev">
                  {col.description}
                </td>
              </tr>)}
          </tbody>
        </table>
      </div>
      {table.indexes && table.indexes.length > 0 && <div className="bg-slate-50 p-4 border-t border-slate-200" data-id="element-1258" data-mp="3758lygxhgkvi">
          <h4 className="text-xs font-semibold text-slate-500 uppercase mb-2" data-id="element-1259" data-mp="3784ka6d8tbkh">
            Indexes
          </h4>
          <ul className="space-y-1" data-id="element-1260" data-mp="mft080d4fmcq">
            {table.indexes.map((idx, i) => <li key={i} className="text-xs font-mono text-slate-600 flex items-start" data-id="element-1261" data-mp="mhvlk73bwrb4" data-mpi={i}>
                <span className="text-slate-400 mr-2" data-id="element-1262" data-mp="yyeuw9wuok29">-</span> {idx}
              </li>)}
          </ul>
        </div>}
    </div>;
}
// --- Sections ---
function ConventionsSection() {
  return <div className="p-8" data-id="element-1263" data-mp="p10iqmhymhfr">
      <div className="mb-8 border-b border-slate-200 pb-4" data-id="element-1264" data-mp="p08ujwqozlzi">
        <h2 className="text-xl font-semibold text-slate-900" data-id="element-1265" data-mp="1cp9zha0bgf01">
          Conventions & PostgreSQL Optimization
        </h2>
        <p className="text-slate-500 mt-1" data-id="element-1266" data-mp="1ho4q2y4iz7up">
          Global standards applied across the entire database schema.
        </p>
      </div>

      <div className="space-y-8" data-id="element-1267" data-mp="17ytixh178d9g">
        <section data-id="element-1268" data-mp="17y1m62vv89hl">
          <h3 className="text-lg font-medium text-slate-900 mb-4 flex items-center" data-id="element-1269" data-mp="17xidzdw0ozgr">
            <Settings className="h-5 w-5 mr-2 text-slate-400" data-id="element-1270" data-mp="1vncq1t8iijzy" />
            Global Schema Conventions
          </h3>
          <ul className="space-y-3 text-sm text-slate-600 list-disc list-inside ml-4" data-id="element-1271" data-mp="20gp2l4zx30cc">
            <li data-id="element-1272" data-mp="1mzq1j91ootx3">
              <strong data-id="element-1273" data-mp="1myy8a4vk789z">Primary Keys:</strong> All tables use <code data-id="element-1274" data-mp="1rx7zk4e5xoqb">UUID</code>{' '}
              primary keys (specifically UUIDv7 for sequential ordering and
              better index locality).
            </li>
            <li data-id="element-1275" data-mp="1d2blvbnl495u">
              <strong data-id="element-1276" data-mp="1hzbr5kcm17vw">Multi-tenancy:</strong> All tenant-scoped tables include
              an <code data-id="element-1277" data-mp="1i1es2zfawlfe">organization_id</code> foreign key.
            </li>
            <li data-id="element-1278" data-mp="1475ck2p3v8yk">
              <strong data-id="element-1279" data-mp="146m6bj8ti90d">Timestamps:</strong> Every table includes{' '}
              <code data-id="element-1280" data-mp="18zozvrdlhigk">created_at</code> and <code data-id="element-1281" data-mp="18zygiocr2d9l">updated_at</code> (type{' '}
              <code data-id="element-1282" data-mp="ua9cmld9vv5m">timestamptz</code>, defaulting to <code data-id="element-1283" data-mp="ubaogs244loe">now()</code>).
            </li>
            <li data-id="element-1284" data-mp="z8kmux7hc7od">
              <strong data-id="element-1285" data-mp="z813dc26lb88">Soft Deletes:</strong> Records are rarely hard-deleted. We
              use a <code data-id="element-1286" data-mp="l9b881yx9lmi">deleted_at</code> (timestamptz, nullable) column.
            </li>
            <li data-id="element-1287" data-mp="pamy88lpk8v8">
              <strong data-id="element-1288" data-mp="pb66sypgxkwl">Audit Fields:</strong> Most tables include{' '}
              <code data-id="element-1289" data-mp="pcha6xf04qn6">created_by</code> and <code data-id="element-1290" data-mp="6lubylzc74o9">updated_by</code> foreign keys
              pointing to the <code data-id="element-1291" data-mp="6ogkooxyrq82">users</code> table.
            </li>
            <li data-id="element-1292" data-mp="1ob01sq433zh">
              <strong data-id="element-1293" data-mp="1ou4q7qr3vvk">Naming:</strong> <code data-id="element-1294" data-mp="1pcdg3ulo2kd">snake_case</code> for all tables
              and columns. Table names are plural.
            </li>
            <li data-id="element-1295" data-mp="anxqjjhaypdr">
              <strong data-id="element-1296" data-mp="anfvuraagj7y">Enums:</strong> Native PostgreSQL <code data-id="element-1297" data-mp="amebfw7dukdk">ENUM</code> types
              are used for finite, static lists (e.g., status, types).
            </li>
          </ul>
        </section>

        <section data-id="element-1298" data-mp="kmnb5k0l2nxp">
          <h3 className="text-lg font-medium text-slate-900 mb-4 flex items-center" data-id="element-1299" data-mp="kl2g7tacx2zb">
            <Database className="h-5 w-5 mr-2 text-slate-400" data-id="element-1300" data-mp="yds09s6brxts" />
            PostgreSQL Optimization & Scalability
          </h3>
          <div className="bg-slate-50 border border-slate-200 rounded-lg p-5 space-y-4 text-sm text-slate-700" data-id="element-1301" data-mp="ucgksfh60szb">
            <p data-id="element-1302" data-mp="18bzei398c1s3">
              <strong data-id="element-1303" data-mp="189d8i81zlubz">Indexing Strategy:</strong>
            </p>
            <ul className="list-disc list-inside ml-4 space-y-1" data-id="element-1304" data-mp="13dy0hwqnusnh">
              <li data-id="element-1305" data-mp="zqk8hf1scx4">
                Every Foreign Key is explicitly indexed to prevent full table
                scans during joins.
              </li>
              <li data-id="element-1306" data-mp="5126qe2mki13">
                Composite indexes are created for common query patterns, always
                leading with <code data-id="element-1307" data-mp="9y2a2h83b6oi">organization_id</code> for multi-tenant
                isolation (e.g.,{' '}
                <code data-id="element-1308" data-mp="esgaiy1m0401">
                  CREATE INDEX idx_users_org_status ON users(organization_id,
                  status)
                </code>
                ).
              </li>
              <li data-id="element-1309" data-mp="oqw18zwq5b90">
                Partial indexes are used to exclude soft-deleted records (e.g.,{' '}
                <code data-id="element-1310" data-mp="soa66fddgwv7">WHERE deleted_at IS NULL</code>).
              </li>
            </ul>
            <p className="mt-4" data-id="element-1311" data-mp="xoog62yjagvv">
              <strong data-id="element-1312" data-mp="2uhzpx8pswz03">Partitioning:</strong>
            </p>
            <ul className="list-disc list-inside ml-4 space-y-1" data-id="element-1313" data-mp="2ujkmf3voes0l">
              <li data-id="element-1314" data-mp="2zhvvhce5cyhr">
                The <code data-id="element-1315" data-mp="2zhcmwmadzmge">audit_logs</code> and <code data-id="element-1316" data-mp="2zinrupfkj093">responses</code> tables
                are candidates for declarative partitioning by{' '}
                <code data-id="element-1317" data-mp="2n6lewbnbxz9j">created_at</code> (e.g., monthly partitions) to manage
                high volume and facilitate easy archiving.
              </li>
            </ul>
            <p className="mt-4" data-id="element-1318" data-mp="2i7ryaacqxs55">
              <strong data-id="element-1319" data-mp="2d8p3rjue74bf">JSONB Usage:</strong>
            </p>
            <ul className="list-disc list-inside ml-4 space-y-1" data-id="element-1320" data-mp="2da8l7drsbles">
              <li data-id="element-1321" data-mp="3awfmoesskxmx">
                <code data-id="element-1322" data-mp="3avnsn8euf7yr">JSONB</code> columns are used sparingly for truly
                flexible, schema-less data (e.g.,{' '}
                <code data-id="element-1323" data-mp="35v01mmqm6r4j">ai_settings.config</code>,{' '}
                <code data-id="element-1324" data-mp="30wykw8gnv3w8">audit_logs.metadata</code>) and are indexed using GIN
                indexes when querying within the JSON is required.
              </li>
            </ul>
          </div>
        </section>
      </div>
    </div>;
}
function IdentitySection() {
  const tables: TableDef[] = [{
    name: 'organizations',
    description: 'The root tenant entity. All data is scoped to an organization.',
    columns: [{
      name: 'id',
      type: 'uuid',
      isPk: true,
      description: 'Primary Key'
    }, {
      name: 'name',
      type: 'varchar(255)',
      description: 'Organization name'
    }, {
      name: 'slug',
      type: 'varchar(255)',
      isUnique: true,
      description: 'URL-friendly identifier'
    }, {
      name: 'status',
      type: 'enum(active, trial, suspended)',
      description: 'Account status'
    }, {
      name: 'primary_color',
      type: 'varchar(7)',
      isNullable: true,
      description: 'Hex color for branding'
    }, {
      name: 'logo_url',
      type: 'text',
      isNullable: true,
      description: 'URL to logo image'
    }, {
      name: 'timezone',
      type: 'varchar(50)',
      description: 'Default timezone'
    }, {
      name: 'created_at',
      type: 'timestamptz',
      description: 'Creation timestamp'
    }, {
      name: 'updated_at',
      type: 'timestamptz',
      description: 'Last update timestamp'
    }, {
      name: 'deleted_at',
      type: 'timestamptz',
      isNullable: true,
      description: 'Soft delete timestamp'
    }],
    indexes: ['CREATE UNIQUE INDEX idx_orgs_slug ON organizations(slug) WHERE deleted_at IS NULL']
  }, {
    name: 'users',
    description: 'Individuals who can log in to the platform (Admins, Managers, etc.).',
    columns: [{
      name: 'id',
      type: 'uuid',
      isPk: true,
      description: 'Primary Key'
    }, {
      name: 'organization_id',
      type: 'uuid',
      isFk: true,
      description: 'Tenant ID'
    }, {
      name: 'email',
      type: 'varchar(255)',
      description: 'User email address'
    }, {
      name: 'password_hash',
      type: 'varchar(255)',
      description: 'Bcrypt/Argon2 hash'
    }, {
      name: 'full_name',
      type: 'varchar(255)',
      description: 'User full name'
    }, {
      name: 'title',
      type: 'varchar(255)',
      isNullable: true,
      description: 'Job title'
    }, {
      name: 'status',
      type: 'enum(active, invited, suspended)',
      description: 'Account status'
    }, {
      name: 'avatar_url',
      type: 'text',
      isNullable: true,
      description: 'URL to avatar image'
    }, {
      name: 'last_active_at',
      type: 'timestamptz',
      isNullable: true,
      description: 'Last login/activity'
    }, {
      name: 'created_at',
      type: 'timestamptz',
      description: 'Creation timestamp'
    }, {
      name: 'deleted_at',
      type: 'timestamptz',
      isNullable: true,
      description: 'Soft delete timestamp'
    }],
    indexes: ['CREATE UNIQUE INDEX idx_users_org_email ON users(organization_id, email) WHERE deleted_at IS NULL', 'CREATE INDEX idx_users_org_id ON users(organization_id)']
  }, {
    name: 'roles',
    description: 'Defines a set of permissions. Can be system-defined or custom per organization.',
    columns: [{
      name: 'id',
      type: 'uuid',
      isPk: true,
      description: 'Primary Key'
    }, {
      name: 'organization_id',
      type: 'uuid',
      isFk: true,
      isNullable: true,
      description: 'Null for system roles'
    }, {
      name: 'name',
      type: 'varchar(100)',
      description: 'Role name (e.g., Administrator)'
    }, {
      name: 'is_system',
      type: 'boolean',
      description: 'True if uneditable system role'
    }]
  }, {
    name: 'user_roles',
    description: 'Join table assigning roles to users.',
    columns: [{
      name: 'user_id',
      type: 'uuid',
      isPk: true,
      isFk: true,
      description: 'User ID'
    }, {
      name: 'role_id',
      type: 'uuid',
      isPk: true,
      isFk: true,
      description: 'Role ID'
    }],
    indexes: ['CREATE INDEX idx_user_roles_role_id ON user_roles(role_id)']
  }];
  return <div className="p-8" data-id="element-1325" data-mp="3tsrxl84gbz">
      <div className="mb-8 border-b border-slate-200 pb-4" data-id="element-1326" data-mp="3aj145q8y91">
        <h2 className="text-xl font-semibold text-slate-900" data-id="element-1327" data-mp="1qeyev8dx01g1">
          Identity & Access
        </h2>
        <p className="text-slate-500 mt-1" data-id="element-1328" data-mp="1uh37q4wjgu1m">
          Core tables managing multi-tenancy, user accounts, and role-based
          access control (RBAC).
        </p>
      </div>
      {tables.map(t => <TableCard key={t.name} table={t} data-id="element-1329" data-mp="3aya935gfedzg" />)}
    </div>;
}
function ContentSection() {
  const tables: TableDef[] = [{
    name: 'competencies',
    description: 'The core skills and behaviors measured in assessments.',
    columns: [{
      name: 'id',
      type: 'uuid',
      isPk: true,
      description: 'Primary Key'
    }, {
      name: 'organization_id',
      type: 'uuid',
      isFk: true,
      description: 'Tenant ID'
    }, {
      name: 'name',
      type: 'varchar(255)',
      description: 'Competency name'
    }, {
      name: 'description',
      type: 'text',
      description: 'Detailed description'
    }, {
      name: 'category',
      type: 'varchar(100)',
      description: 'Grouping category'
    }, {
      name: 'status',
      type: 'enum(active, archived)',
      description: 'Availability status'
    }, {
      name: 'created_by',
      type: 'uuid',
      isFk: true,
      description: 'Creator User ID'
    }, {
      name: 'created_at',
      type: 'timestamptz',
      description: 'Creation timestamp'
    }, {
      name: 'deleted_at',
      type: 'timestamptz',
      isNullable: true,
      description: 'Soft delete timestamp'
    }],
    indexes: ['CREATE INDEX idx_competencies_org_status ON competencies(organization_id, status)']
  }, {
    name: 'competency_levels',
    description: 'Maps competencies to the leadership levels they apply to. No rows for a competency means it applies to every level.',
    columns: [{
      name: 'competency_id',
      type: 'uuid',
      isPk: true,
      isFk: true,
      description: 'Composite PK / FK to competencies'
    }, {
      name: 'level',
      type: 'enum(individual_contributor, team_lead, manager, director, vp, executive)',
      isPk: true,
      description: 'Composite PK — leadership level'
    }],
    indexes: ['CREATE INDEX idx_competency_levels_level ON competency_levels(level)']
  }, {
    name: 'questions',
    description: 'Individual questions linked to competencies.',
    columns: [{
      name: 'id',
      type: 'uuid',
      isPk: true,
      description: 'Primary Key'
    }, {
      name: 'organization_id',
      type: 'uuid',
      isFk: true,
      description: 'Tenant ID'
    }, {
      name: 'competency_id',
      type: 'uuid',
      isFk: true,
      isNullable: true,
      description: 'Linked competency'
    }, {
      name: 'text',
      type: 'text',
      description: 'The question text'
    }, {
      name: 'rating_type',
      type: 'enum(five_point, ten_point, multiple_choice, open_text)',
      description: 'Input type'
    }, {
      name: 'is_template',
      type: 'boolean',
      description: 'True if available in library'
    }, {
      name: 'status',
      type: 'enum(active, archived)',
      description: 'Availability status'
    }],
    indexes: ['CREATE INDEX idx_questions_org_comp ON questions(organization_id, competency_id)']
  }, {
    name: 'question_options',
    description: 'Available choices for multiple_choice questions.',
    columns: [{
      name: 'id',
      type: 'uuid',
      isPk: true,
      description: 'Primary Key'
    }, {
      name: 'question_id',
      type: 'uuid',
      isFk: true,
      description: 'Parent question'
    }, {
      name: 'label',
      type: 'varchar(255)',
      description: 'Display text'
    }, {
      name: 'value',
      type: 'varchar(255)',
      description: 'Underlying value'
    }, {
      name: 'sort_order',
      type: 'integer',
      description: 'Display order'
    }]
  }];
  return <div className="p-8" data-id="element-1330" data-mp="s18ewprepx65">
      <div className="mb-8 border-b border-slate-200 pb-4" data-id="element-1331" data-mp="rxualbz6ndw2">
        <h2 className="text-xl font-semibold text-slate-900" data-id="element-1332" data-mp="w2k1sniwc4an">
          Content Library
        </h2>
        <p className="text-slate-500 mt-1" data-id="element-1333" data-mp="10ztqg0ygobxf">
          Management of reusable competencies, questions, and rating scales.
        </p>
      </div>
      {tables.map(t => <TableCard key={t.name} table={t} data-id="element-1334" data-mp="8chh4shba0m2" />)}
    </div>;
}
function AssessmentsSection() {
  const tables: TableDef[] = [{
    name: 'assessments',
    description: 'An active assessment cycle or campaign.',
    columns: [{
      name: 'id',
      type: 'uuid',
      isPk: true,
      description: 'Primary Key'
    }, {
      name: 'organization_id',
      type: 'uuid',
      isFk: true,
      description: 'Tenant ID'
    }, {
      name: 'name',
      type: 'varchar(255)',
      description: 'Cycle name (e.g., Q3 Review)'
    }, {
      name: 'type',
      type: 'enum(self, 360, manager, peer, pulse)',
      description: 'Assessment type'
    }, {
      name: 'status',
      type: 'enum(draft, active, completed, archived)',
      description: 'Current state'
    }, {
      name: 'anonymity',
      type: 'enum(anonymous, named)',
      description: 'Reporting anonymity rule'
    }, {
      name: 'starts_at',
      type: 'timestamptz',
      isNullable: true,
      description: 'Launch date'
    }, {
      name: 'ends_at',
      type: 'timestamptz',
      isNullable: true,
      description: 'Deadline'
    }, {
      name: 'created_by',
      type: 'uuid',
      isFk: true,
      description: 'Creator User ID'
    }, {
      name: 'created_at',
      type: 'timestamptz',
      description: 'Creation timestamp'
    }, {
      name: 'deleted_at',
      type: 'timestamptz',
      isNullable: true,
      description: 'Soft delete timestamp'
    }],
    indexes: ['CREATE INDEX idx_assessments_org_status ON assessments(organization_id, status)']
  }, {
    name: 'assessment_subjects',
    description: 'The individuals being evaluated in an assessment cycle.',
    columns: [{
      name: 'id',
      type: 'uuid',
      isPk: true,
      description: 'Primary Key'
    }, {
      name: 'assessment_id',
      type: 'uuid',
      isFk: true,
      description: 'Parent assessment'
    }, {
      name: 'user_id',
      type: 'uuid',
      isFk: true,
      description: 'The person being evaluated'
    }],
    indexes: ['CREATE UNIQUE INDEX idx_assessment_subjects_unique ON assessment_subjects(assessment_id, user_id)']
  }, {
    name: 'assessment_questions',
    description: 'A snapshot of questions included in a specific assessment.',
    columns: [{
      name: 'id',
      type: 'uuid',
      isPk: true,
      description: 'Primary Key'
    }, {
      name: 'assessment_id',
      type: 'uuid',
      isFk: true,
      description: 'Parent assessment'
    }, {
      name: 'question_id',
      type: 'uuid',
      isFk: true,
      isNullable: true,
      description: 'Source question (if from library)'
    }, {
      name: 'competency_id',
      type: 'uuid',
      isFk: true,
      description: 'Linked competency'
    }, {
      name: 'text',
      type: 'text',
      description: 'Snapshot of question text'
    }, {
      name: 'sort_order',
      type: 'integer',
      description: 'Display order'
    }]
  }];
  return <div className="p-8" data-id="element-1335" data-mp="3rcc6743q8p8q">
      <div className="mb-8 border-b border-slate-200 pb-4" data-id="element-1336" data-mp="3rasm12ey9u1o">
        <h2 className="text-xl font-semibold text-slate-900" data-id="element-1337" data-mp="59f0cnw2iyi">Assessments</h2>
        <p className="text-slate-500 mt-1" data-id="element-1338" data-mp="4wr70b7xp97c">
          Defines assessment cycles, the subjects being evaluated, and the
          specific questions asked.
        </p>
      </div>
      {tables.map(t => <TableCard key={t.name} table={t} data-id="element-1339" data-mp="ew9o8jwgmgvp" />)}
    </div>;
}
function ResponsesSection() {
  const tables: TableDef[] = [{
    name: 'respondents',
    description: 'Individuals providing feedback for a specific subject.',
    columns: [{
      name: 'id',
      type: 'uuid',
      isPk: true,
      description: 'Primary Key'
    }, {
      name: 'assessment_id',
      type: 'uuid',
      isFk: true,
      description: 'Parent assessment'
    }, {
      name: 'subject_id',
      type: 'uuid',
      isFk: true,
      description: 'The person they are evaluating'
    }, {
      name: 'user_id',
      type: 'uuid',
      isFk: true,
      isNullable: true,
      description: 'Internal user ID (if applicable)'
    }, {
      name: 'email',
      type: 'varchar(255)',
      isNullable: true,
      description: 'External respondent email'
    }, {
      name: 'relationship',
      type: 'enum(self, manager, peer, direct_report, external)',
      description: 'Relationship to subject'
    }, {
      name: 'status',
      type: 'enum(pending, opened, started, completed, expired)',
      description: 'Invitation/completion status'
    }, {
      name: 'token_hash',
      type: 'varchar(255)',
      isUnique: true,
      description: 'Secure link token'
    }],
    indexes: ['CREATE INDEX idx_respondents_assessment_subject ON respondents(assessment_id, subject_id)', 'CREATE INDEX idx_respondents_token ON respondents(token_hash)']
  }, {
    name: 'responses',
    description: 'Individual answers provided by respondents.',
    columns: [{
      name: 'id',
      type: 'uuid',
      isPk: true,
      description: 'Primary Key'
    }, {
      name: 'respondent_id',
      type: 'uuid',
      isFk: true,
      description: 'The person answering'
    }, {
      name: 'assessment_question_id',
      type: 'uuid',
      isFk: true,
      description: 'The question answered'
    }, {
      name: 'numeric_value',
      type: 'numeric(5,2)',
      isNullable: true,
      description: 'Score (for scaled questions)'
    }, {
      name: 'text_value',
      type: 'text',
      isNullable: true,
      description: 'Answer (for open text)'
    }, {
      name: 'created_at',
      type: 'timestamptz',
      description: 'Submission timestamp'
    }],
    indexes: ['CREATE UNIQUE INDEX idx_responses_unique ON responses(respondent_id, assessment_question_id)']
  }, {
    name: 'scores',
    description: 'Pre-computed aggregate scores for fast reporting.',
    columns: [{
      name: 'id',
      type: 'uuid',
      isPk: true,
      description: 'Primary Key'
    }, {
      name: 'assessment_id',
      type: 'uuid',
      isFk: true,
      description: 'Parent assessment'
    }, {
      name: 'subject_id',
      type: 'uuid',
      isFk: true,
      description: 'The person evaluated'
    }, {
      name: 'competency_id',
      type: 'uuid',
      isFk: true,
      isNullable: true,
      description: 'Null indicates overall score'
    }, {
      name: 'score_type',
      type: 'enum(self, others, weighted, overall)',
      description: 'Type of aggregate'
    }, {
      name: 'rater_group',
      type: 'enum(self, manager, peers, directs, all)',
      description: 'Group providing the score'
    }, {
      name: 'value',
      type: 'numeric(5,2)',
      description: 'Computed score value'
    }, {
      name: 'computed_at',
      type: 'timestamptz',
      description: 'When this was calculated'
    }],
    indexes: ['CREATE INDEX idx_scores_lookup ON scores(assessment_id, subject_id, competency_id)']
  }];
  return <div className="p-8" data-id="element-1340" data-mp="1qm283oczds6q">
      <div className="mb-8 border-b border-slate-200 pb-4" data-id="element-1341" data-mp="1qj7mvum583c9">
        <h2 className="text-xl font-semibold text-slate-900" data-id="element-1342" data-mp="1bp34scp5wli2">
          Responses & Scoring
        </h2>
        <p className="text-slate-500 mt-1" data-id="element-1343" data-mp="1go6198r2tjef">
          Tracks respondent invitations, raw answers, and pre-computed aggregate
          scores.
        </p>
      </div>
      {tables.map(t => <TableCard key={t.name} table={t} data-id="element-1344" data-mp="29aqqa2k2xjet" />)}
    </div>;
}
function ReportingSection() {
  const tables: TableDef[] = [{
    name: 'reports',
    description: 'Generated PDF reports and their metadata.',
    columns: [{
      name: 'id',
      type: 'uuid',
      isPk: true,
      description: 'Primary Key'
    }, {
      name: 'organization_id',
      type: 'uuid',
      isFk: true,
      description: 'Tenant ID'
    }, {
      name: 'assessment_id',
      type: 'uuid',
      isFk: true,
      description: 'Source assessment'
    }, {
      name: 'subject_id',
      type: 'uuid',
      isFk: true,
      isNullable: true,
      description: 'Subject (null for aggregate)'
    }, {
      name: 'type',
      type: 'enum(individual, team_aggregate, executive_summary)',
      description: 'Report type'
    }, {
      name: 'file_url',
      type: 'text',
      description: 'S3/CDN link to PDF'
    }, {
      name: 'status',
      type: 'enum(generating, ready, archived)',
      description: 'Generation status'
    }, {
      name: 'generated_at',
      type: 'timestamptz',
      description: 'Completion timestamp'
    }]
  }, {
    name: 'ai_insights',
    description: 'AI-generated analysis based on assessment data.',
    columns: [{
      name: 'id',
      type: 'uuid',
      isPk: true,
      description: 'Primary Key'
    }, {
      name: 'organization_id',
      type: 'uuid',
      isFk: true,
      description: 'Tenant ID'
    }, {
      name: 'report_id',
      type: 'uuid',
      isFk: true,
      isNullable: true,
      description: 'Linked report'
    }, {
      name: 'subject_id',
      type: 'uuid',
      isFk: true,
      description: 'Subject of insight'
    }, {
      name: 'kind',
      type: 'enum(executive_summary, strength, development, blind_spot, action_plan)',
      description: 'Type of insight'
    }, {
      name: 'content',
      type: 'jsonb',
      description: 'Structured AI output'
    }, {
      name: 'model_version',
      type: 'varchar(50)',
      description: 'LLM version used'
    }, {
      name: 'created_at',
      type: 'timestamptz',
      description: 'Generation timestamp'
    }]
  }];
  return <div className="p-8" data-id="element-1345" data-mp="ufimpriqr5m4">
      <div className="mb-8 border-b border-slate-200 pb-4" data-id="element-1346" data-mp="ugtqvsg3mfdr">
        <h2 className="text-xl font-semibold text-slate-900" data-id="element-1347" data-mp="pgp97z3gfely">Reporting & AI</h2>
        <p className="text-slate-500 mt-1" data-id="element-1348" data-mp="pis9rd28gq4x">
          Stores generated report artifacts and structured AI insights.
        </p>
      </div>
      {tables.map(t => <TableCard key={t.name} table={t} data-id="element-1349" data-mp="1i6o3c8wia1xh" />)}
    </div>;
}
function BillingSection() {
  const tables: TableDef[] = [{
    name: 'plans',
    description: 'Available subscription tiers.',
    columns: [{
      name: 'id',
      type: 'uuid',
      isPk: true,
      description: 'Primary Key'
    }, {
      name: 'name',
      type: 'varchar(100)',
      description: 'Plan name (e.g., Enterprise)'
    }, {
      name: 'price_cents',
      type: 'integer',
      description: 'Price in cents'
    }, {
      name: 'interval',
      type: 'enum(month, year)',
      description: 'Billing frequency'
    }, {
      name: 'features',
      type: 'jsonb',
      description: 'Feature flags/limits'
    }]
  }, {
    name: 'subscriptions',
    description: 'Organization subscription status.',
    columns: [{
      name: 'id',
      type: 'uuid',
      isPk: true,
      description: 'Primary Key'
    }, {
      name: 'organization_id',
      type: 'uuid',
      isFk: true,
      description: 'Tenant ID'
    }, {
      name: 'plan_id',
      type: 'uuid',
      isFk: true,
      description: 'Selected plan'
    }, {
      name: 'status',
      type: 'enum(trialing, active, past_due, canceled)',
      description: 'Subscription state'
    }, {
      name: 'stripe_customer_id',
      type: 'varchar(255)',
      description: 'External billing ID'
    }, {
      name: 'current_period_end',
      type: 'timestamptz',
      description: 'Renewal date'
    }]
  }];
  return <div className="p-8" data-id="element-1350" data-mp="2vaxxon21pghy">
      <div className="mb-8 border-b border-slate-200 pb-4" data-id="element-1351" data-mp="393nmt0cvf1ja">
        <h2 className="text-xl font-semibold text-slate-900" data-id="element-1352" data-mp="3922vrkprm0q6">
          Subscriptions & Billing (Future)
        </h2>
        <p className="text-slate-500 mt-1" data-id="element-1353" data-mp="346nkn0j56ixg">
          Schema for planned SaaS billing integration.
        </p>
      </div>
      {tables.map(t => <TableCard key={t.name} table={t} data-id="element-1354" data-mp="3e3s6ez92scdq" />)}
    </div>;
}
function MarketplaceSection() {
  const tables: TableDef[] = [{
    name: 'consultants',
    description: 'Profiles for external coaches/consultants.',
    columns: [{
      name: 'id',
      type: 'uuid',
      isPk: true,
      description: 'Primary Key'
    }, {
      name: 'user_id',
      type: 'uuid',
      isFk: true,
      description: 'Linked user account'
    }, {
      name: 'headline',
      type: 'varchar(255)',
      description: 'Profile headline'
    }, {
      name: 'bio',
      type: 'text',
      description: 'Detailed biography'
    }, {
      name: 'hourly_rate_cents',
      type: 'integer',
      description: 'Billing rate'
    }, {
      name: 'verified',
      type: 'boolean',
      description: 'Platform vetted status'
    }]
  }, {
    name: 'engagements',
    description: 'Active contracts between organizations and consultants.',
    columns: [{
      name: 'id',
      type: 'uuid',
      isPk: true,
      description: 'Primary Key'
    }, {
      name: 'organization_id',
      type: 'uuid',
      isFk: true,
      description: 'Client tenant'
    }, {
      name: 'consultant_id',
      type: 'uuid',
      isFk: true,
      description: 'Hired consultant'
    }, {
      name: 'subject_id',
      type: 'uuid',
      isFk: true,
      isNullable: true,
      description: 'Specific coachee (if applicable)'
    }, {
      name: 'status',
      type: 'enum(requested, active, completed, canceled)',
      description: 'Contract state'
    }]
  }];
  return <div className="p-8" data-id="element-1355" data-mp="2x30nux3sfsx3">
      <div className="mb-8 border-b border-slate-200 pb-4" data-id="element-1356" data-mp="2x4bs0y15b2oq">
        <h2 className="text-xl font-semibold text-slate-900" data-id="element-1357" data-mp="2s4pe0mdhtiev">
          Consultant Marketplace (Future)
        </h2>
        <p className="text-slate-500 mt-1" data-id="element-1358" data-mp="vachwfzrerdm">
          Schema for connecting organizations with external executive coaches.
        </p>
      </div>
      {tables.map(t => <TableCard key={t.name} table={t} data-id="element-1359" data-mp="4gc60eakwdrn" />)}
    </div>;
}
function BenchmarkingSection() {
  const tables: TableDef[] = [{
    name: 'benchmark_datasets',
    description: 'Aggregated industry data sets.',
    columns: [{
      name: 'id',
      type: 'uuid',
      isPk: true,
      description: 'Primary Key'
    }, {
      name: 'name',
      type: 'varchar(255)',
      description: 'Dataset name (e.g., Tech 2023)'
    }, {
      name: 'industry',
      type: 'varchar(100)',
      description: 'Target industry'
    }, {
      name: 'is_public',
      type: 'boolean',
      description: 'Available to all tenants'
    }]
  }, {
    name: 'benchmark_metrics',
    description: 'Statistical percentiles for specific competencies.',
    columns: [{
      name: 'id',
      type: 'uuid',
      isPk: true,
      description: 'Primary Key'
    }, {
      name: 'dataset_id',
      type: 'uuid',
      isFk: true,
      description: 'Parent dataset'
    }, {
      name: 'competency_id',
      type: 'uuid',
      isFk: true,
      description: 'Measured competency'
    }, {
      name: 'percentile_50',
      type: 'numeric(5,2)',
      description: 'Median score'
    }, {
      name: 'percentile_75',
      type: 'numeric(5,2)',
      description: 'Top quartile score'
    }, {
      name: 'percentile_90',
      type: 'numeric(5,2)',
      description: 'Top decile score'
    }, {
      name: 'sample_size',
      type: 'integer',
      description: 'Number of data points'
    }]
  }];
  return <div className="p-8" data-id="element-1360" data-mp="3lfublajtpz5a">
      <div className="mb-8 border-b border-slate-200 pb-4" data-id="element-1361" data-mp="3qg8j8rhij72s">
        <h2 className="text-xl font-semibold text-slate-900" data-id="element-1362" data-mp="3qenkepo611z4">
          Benchmarking (Future)
        </h2>
        <p className="text-slate-500 mt-1" data-id="element-1363" data-mp="1qc5xja53xu4z">
          Schema for comparing internal scores against industry standards.
        </p>
      </div>
      {tables.map(t => <TableCard key={t.name} table={t} data-id="element-1364" data-mp="205wnkjgvucms" />)}
    </div>;
}
function CoachingSection() {
  const tables: TableDef[] = [{
    name: 'coaching_plans',
    description: 'Development plans generated by AI or consultants.',
    columns: [{
      name: 'id',
      type: 'uuid',
      isPk: true,
      description: 'Primary Key'
    }, {
      name: 'subject_id',
      type: 'uuid',
      isFk: true,
      description: 'The coachee'
    }, {
      name: 'generated_by',
      type: 'enum(ai, consultant, self)',
      description: 'Source of plan'
    }, {
      name: 'status',
      type: 'enum(active, completed)',
      description: 'Plan state'
    }]
  }, {
    name: 'coaching_actions',
    description: 'Specific tasks within a coaching plan.',
    columns: [{
      name: 'id',
      type: 'uuid',
      isPk: true,
      description: 'Primary Key'
    }, {
      name: 'coaching_plan_id',
      type: 'uuid',
      isFk: true,
      description: 'Parent plan'
    }, {
      name: 'title',
      type: 'varchar(255)',
      description: 'Task title'
    }, {
      name: 'status',
      type: 'enum(open, in_progress, done)',
      description: 'Task state'
    }, {
      name: 'due_at',
      type: 'timestamptz',
      isNullable: true,
      description: 'Target completion date'
    }]
  }];
  return <div className="p-8" data-id="element-1365" data-mp="vaejj89vmfd">
      <div className="mb-8 border-b border-slate-200 pb-4" data-id="element-1366" data-mp="5w7xhy13wkhx">
        <h2 className="text-xl font-semibold text-slate-900" data-id="element-1367" data-mp="5vooj6tfp6g1">
          AI Coaching (Future)
        </h2>
        <p className="text-slate-500 mt-1" data-id="element-1368" data-mp="21xibslj6scyf">
          Schema for tracking ongoing development and coaching tasks.
        </p>
      </div>
      {tables.map(t => <TableCard key={t.name} table={t} data-id="element-1369" data-mp="1sxv63njfckzk" />)}
    </div>;
}
function AuditSection() {
  const tables: TableDef[] = [{
    name: 'audit_logs',
    description: 'Immutable record of system events for security and compliance.',
    columns: [{
      name: 'id',
      type: 'uuid',
      isPk: true,
      description: 'Primary Key'
    }, {
      name: 'organization_id',
      type: 'uuid',
      isFk: true,
      description: 'Tenant ID'
    }, {
      name: 'actor_user_id',
      type: 'uuid',
      isFk: true,
      isNullable: true,
      description: 'User performing action (null for system)'
    }, {
      name: 'action',
      type: 'varchar(100)',
      description: 'Event type (e.g., user.login, report.generate)'
    }, {
      name: 'entity_type',
      type: 'varchar(100)',
      description: 'Resource affected (e.g., assessment)'
    }, {
      name: 'entity_id',
      type: 'uuid',
      isNullable: true,
      description: 'ID of affected resource'
    }, {
      name: 'ip_address',
      type: 'varchar(45)',
      isNullable: true,
      description: 'Source IP'
    }, {
      name: 'metadata',
      type: 'jsonb',
      isNullable: true,
      description: 'Additional context (e.g., previous state)'
    }, {
      name: 'created_at',
      type: 'timestamptz',
      description: 'Event timestamp'
    }],
    indexes: ['CREATE INDEX idx_audit_logs_org_time ON audit_logs(organization_id, created_at)', '-- Note: Candidate for table partitioning by created_at (e.g., monthly)']
  }];
  return <div className="p-8" data-id="element-1370" data-mp="3cropftz39d9q">
      <div className="mb-8 border-b border-slate-200 pb-4" data-id="element-1371" data-mp="37vhrancxp62c">
        <h2 className="text-xl font-semibold text-slate-900" data-id="element-1372" data-mp="37svgmeuqyafw">Audit & System</h2>
        <p className="text-slate-500 mt-1" data-id="element-1373" data-mp="3mjb2lvuzlmf4">
          Security logging and system-level configuration.
        </p>
      </div>
      {tables.map(t => <TableCard key={t.name} table={t} data-id="element-1374" data-mp="2tungjy2eyt0i" />)}
    </div>;
}